import React, { useState, useEffect } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Table, Button, Space, Select } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";

const { Option } = Select;

const PartyMasterReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyName, setPartyName] = useState(undefined);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchPartyData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (partyName && String(partyName).trim()) {
          params.set("partyName", String(partyName).trim());
        }

        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getPartyMasterReport?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          setData(result.parties || []);
          const names = Array.isArray(result.partyNames) ? result.partyNames : [];
          setPartyOptions(
            names.map((name) => ({
              label: name,
              value: name,
            })),
          );
        } else {
          console.error("Failed to fetch party data:", result.message);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching party data:", error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchPartyData();
  }, [apiBaseUrl, partyName]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("isExcel", "true");
      if (partyName && String(partyName).trim()) {
        params.set("partyName", String(partyName).trim());
      }
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getPartyMasterReport?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Party_Master.xlsx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 200,
      align: "center",
      render: (partyName) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {partyName || "N/A"}
        </span>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      align: "center",
      render: (category) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {category || "N/A"}
        </span>
      ),
    },
    {
      title: "GST Number",
      dataIndex: "gstNumber",
      key: "gstNumber",
      width: 150,
      align: "center",
      render: (gstNumber) => (
        <span
          style={{
            color: "#333",
            fontFamily: "monospace",
            fontSize: "12px",
          }}
        >
          {gstNumber || "N/A"}
        </span>
      ),
    },
    {
      title: "Address 1",
      dataIndex: "add1",
      key: "add1",
      width: 200,
      align: "center",
      render: (add1) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{add1 || "N/A"}</span>
      ),
    },
    {
      title: "Address 2",
      dataIndex: "add2",
      key: "add2",
      width: 200,
      align: "center",
      render: (add2) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{add2 || "N/A"}</span>
      ),
    },
    {
      title: "Address 3",
      dataIndex: "add3",
      key: "add3",
      width: 150,
      align: "center",
      render: (add3) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{add3 || "N/A"}</span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      width: 140,
      align: "center",
      render: (contactNumber) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {contactNumber || "N/A"}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      align: "center",
      render: (createdAt) => {
        if (!createdAt)
          return <span style={{ color: "#333", fontSize: "12px" }}>N/A</span>;
        return (
          <span
            style={{
              color: "#333",
              fontSize: "12px",
            }}
          >
            {moment(createdAt).format("DD-MM-YYYY")}
          </span>
        );
      },
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Party Master Report
            </Space>
            <Space>
              <Select
                showSearch
                allowClear
                placeholder="Filter by Party Name"
                value={partyName}
                onChange={(v) => setPartyName(v)}
                options={partyOptions}
                style={{ width: 320 }}
                filterOption={(input, opt) =>
                  String(opt?.label || "")
                    .toLowerCase()
                    .startsWith(String(input || "").toLowerCase())
                }
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                style={{ marginLeft: "8px" }}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          components={components}
          rowKey="_id"
          pagination={false}
          sticky
          scroll={{ y: 800, x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default PartyMasterReport;
